/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';

import {
    createEmptyUiFilters,
    removeUiFilter,
    setUiFilter,
    toggleUiFilter,
    type SearchFilter,
    type UiFilterOption,
    type UiFiltersState,
} from '../../utils/filters';

/**
 * The search fields the filters dialog edits. Changes stay here until the user
 * shows the results, so nothing outside the dialog changes before then.
 */
export interface PendingFilters {
    /** Filter groups: booking types, campsite filters and trip preferences */
    fg: string[];
    uiFilters: UiFiltersState;
    /** Raw filters. The dialog only changes the `activity_tags` one. */
    filters: SearchFilter[];
    tripPreferences: boolean;
}

export type PendingFiltersAction =
    | { type: 'reset'; pending: PendingFilters }
    | { type: 'toggleUiFilter'; filterName: string; option: UiFilterOption }
    | { type: 'setUiFilter'; filterName: string; option: UiFilterOption }
    | { type: 'removeUiFilter'; filterName: string; option: UiFilterOption }
    | { type: 'setFilterGroups'; fg: string[] }
    | { type: 'clearAll' }
    | { type: 'enableTripPreferences'; preferences: string[] }
    | { type: 'disableTripPreferences'; preferences: string[] }
    | { type: 'replaceTripPreferences'; previous: string[]; next: string[] };

/** Any object with the search fields `getPendingFilters` reads */
interface PendingFiltersSource {
    fg?: unknown;
    uiFilters?: unknown;
    filters?: unknown;
    tripPreferences?: unknown;
}

function isUiFiltersState(value: unknown): value is UiFiltersState {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isSearchFilter(value: unknown): value is SearchFilter {
    return (
        typeof value === 'object' &&
        value !== null &&
        'attribute' in value &&
        typeof value.attribute === 'string' &&
        'values' in value &&
        Array.isArray(value.values)
    );
}

/** The fields the dialog edits, copied from the `search` state */
export function getPendingFilters(search: PendingFiltersSource): PendingFilters {
    return {
        fg: Array.isArray(search.fg)
            ? search.fg.filter((item): item is string => typeof item === 'string')
            : [],
        uiFilters: isUiFiltersState(search.uiFilters)
            ? search.uiFilters
            : createEmptyUiFilters(),
        filters: Array.isArray(search.filters)
            ? search.filters.filter(isSearchFilter)
            : [],
        tripPreferences: search.tripPreferences === true,
    };
}

/** `uiFilters` fields that track how the filters were changed, not what's selected */
const UI_FILTERS_BOOKKEEPING = ['hasFilters', 'lastAppliedFilter'];

/** Selected option keys per filter name, leaving out filter names with none */
function getUiFilterKeys(uiFilters: UiFiltersState) {
    const result: Record<string, unknown> = {};
    Object.entries(uiFilters).forEach(([filterName, value]) => {
        if (UI_FILTERS_BOOKKEEPING.includes(filterName)) {
            return;
        }
        if (value && typeof value === 'object') {
            const keys = Object.keys(value).sort();
            if (keys.length > 0) {
                result[filterName] = keys;
            }
        } else if (value !== undefined) {
            result[filterName] = value;
        }
    });
    return result;
}

/**
 * Values per raw filter attribute. Comma-separated values are split, since the
 * `activity_tags` one lists the tags in the order they were selected.
 */
function getRawFilterValues(filters: SearchFilter[]) {
    const result: Record<string, string[]> = {};
    filters.forEach(({ attribute, values }) => {
        // `isSearchFilter` doesn't check each value, so they may not be strings
        const split = values.flatMap(({ value }) => String(value).split(','));
        result[attribute] = uniq([...(result[attribute] ?? []), ...split]).sort();
    });
    return result;
}

/** What the filters select, ignoring order and bookkeeping */
function getComparableFilters(pending: PendingFilters) {
    return {
        fg: uniq(pending.fg).sort(),
        uiFilters: getUiFilterKeys(pending.uiFilters),
        filters: getRawFilterValues(pending.filters),
        tripPreferences: pending.tripPreferences,
    };
}

/**
 * Whether the pending filters select something different from the applied
 * ones. Undoing a change, e.g. unchecking and checking a filter again, counts
 * as no change even though the order or bookkeeping fields differ.
 */
export function hasPendingChanges(pending: PendingFilters, applied: PendingFilters) {
    return !isEqual(getComparableFilters(pending), getComparableFilters(applied));
}

function withoutItems(list: string[], items: string[]) {
    return list.filter((item) => !items.includes(item));
}

/** Applies a filter change to the pending filters */
export function pendingFiltersReducer(
    state: PendingFilters,
    action: PendingFiltersAction
): PendingFilters {
    switch (action.type) {
        case 'reset':
            return action.pending;

        case 'toggleUiFilter':
            return {
                ...state,
                ...toggleUiFilter(state, action.filterName, action.option),
            };

        case 'setUiFilter':
            return { ...state, ...setUiFilter(state, action.filterName, action.option) };

        case 'removeUiFilter':
            return {
                ...state,
                ...removeUiFilter(state, action.filterName, action.option),
            };

        case 'setFilterGroups':
            return { ...state, fg: action.fg };

        case 'clearAll':
            return {
                fg: [],
                uiFilters: createEmptyUiFilters(),
                filters: state.filters.filter(
                    (filter) => filter.attribute !== 'activity_tags'
                ),
                tripPreferences: false,
            };

        case 'enableTripPreferences':
            return {
                ...state,
                fg: uniq([...state.fg, ...action.preferences]),
                tripPreferences: true,
            };

        case 'disableTripPreferences':
            return {
                ...state,
                fg: uniq(withoutItems(state.fg, action.preferences)),
                tripPreferences: false,
            };

        case 'replaceTripPreferences':
            if (!state.tripPreferences) {
                return state;
            }
            // Saving no preferences turns them off, the same as in the applied search
            return {
                ...state,
                fg: uniq([...withoutItems(state.fg, action.previous), ...action.next]),
                tripPreferences: action.next.length > 0,
            };

        default:
            return state;
    }
}
